/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Star } from 'lucide-react';
import { useTheme } from '../theme';
import { ROUTES } from '../routes';

const StorefrontSection: React.FC = () => {
  const theme = useTheme();

  const stores = [
    { 
      id: 1001, 
      name: 'The Vintage Nook', 
      rating: 4.8, 
      logo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=100&h=100',
      banner: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80&w=600&h=300',
      items: [
        'https://images.unsplash.com/photo-1550009158-9ebf69173e03?auto=format&fit=crop&q=80&w=100&h=100',
        'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&q=80&w=100&h=100',
        'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&q=80&w=100&h=100'
      ]
    },
    { 
      id: 5, 
      name: 'Commercial Hub', 
      rating: 4.6, 
      logo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=100&h=100',
      banner: 'https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?auto=format&fit=crop&q=80&w=600&h=300',
      items: [
        'https://images.unsplash.com/photo-1416870230247-d0e9a02d1461?auto=format&fit=crop&q=80&w=100&h=100',
        'https://images.unsplash.com/photo-1471193945509-9ad0617afabf?auto=format&fit=crop&q=80&w=100&h=100',
        'https://images.unsplash.com/photo-1463936575829-25148e1db1b8?auto=format&fit=crop&q=80&w=100&h=100'
      ]
    },
    { 
      id: 4, 
      name: 'My Little Shop', 
      rating: 5.0, 
      logo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=100&h=100',
      banner: 'https://images.unsplash.com/photo-1498049794561-7780e7231661?auto=format&fit=crop&q=80&w=600&h=300',
      items: [
        'https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&q=80&w=100&h=100',
        'https://images.unsplash.com/photo-1525547719571-a2d4ac8945e2?auto=format&fit=crop&q=80&w=100&h=100',
        'https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?auto=format&fit=crop&q=80&w=100&h=100'
      ]
    },
  ];

  return (
    <section className="py-12 border-t" style={{ borderColor: theme.colors.border }}>
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-bold" style={{ color: theme.colors.text }}>
          Featured Local Shops
        </h2>
        <Link 
          to={ROUTES.SEARCH} 
          className="flex items-center gap-1 font-semibold hover:opacity-80" 
          style={{ color: theme.colors.primary }}
        >
          View All <ChevronRight size={18} />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {stores.map((store) => (
          <Link 
            key={store.id}
            to={`/store/${store.id}`}
            className="group block relative overflow-hidden rounded-2xl border transition-all hover:shadow-xl"
            style={{ 
              backgroundColor: theme.colors.surface, 
              borderColor: theme.colors.border 
            }}
          >
            {/* Banner */}
            <div className="h-32 relative overflow-hidden">
              <img 
                src={store.banner} 
                alt={store.name} 
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/20" />
            </div>

            {/* Logo & Name */}
            <div className="px-6 pb-6 pt-10 relative">
              <div 
                className="absolute -top-8 left-6 w-16 h-16 rounded-xl border-4 overflow-hidden shadow-lg"
                style={{ 
                  backgroundColor: theme.colors.surface, 
                  borderColor: theme.colors.surface 
                }}
              >
                <img src={store.logo} alt={store.name} className="w-full h-full object-cover" />
              </div>

              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold" style={{ color: theme.colors.text }}>
                    {store.name}
                  </h3>
                  <div className="flex items-center gap-1 mt-1">
                    <Star size={14} fill={theme.colors.accent} style={{ color: theme.colors.accent }} />
                    <span className="text-sm font-semibold">{store.rating}</span>
                  </div>
                </div>
                <div className="px-4 py-1.5 rounded-full text-sm font-bold border transition-colors group-hover:bg-primary group-hover:text-white" style={{ borderColor: theme.colors.primary, color: theme.colors.primary }}>
                  Visit Store
                </div>
              </div>

              {/* Top 3 Items Preview (Visible on Hover) */}
              <div className="flex gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                {store.items.map((item, idx) => (
                  <div key={idx} className="w-1/3 aspect-square rounded-lg overflow-hidden border" style={{ borderColor: theme.colors.border }}>
                    <img src={item} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default StorefrontSection;
